import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Lock, LogOut, RefreshCw, ImageOff, Mail, Trash2, FileText, CalendarDays, Users, Plus, Pencil, Phone } from "lucide-react";
import { API } from "../data/site";
import { InvoiceModal } from "../components/InvoiceModal";
import { JobModal } from "../components/JobModal";
import { ClientModal, CLIENT_STATUS_LABELS } from "../components/ClientModal";

const STATUSES = ["new", "contacted", "scheduled", "completed"];

const StatusBadge = ({ status }) => (
    <span className={`rounded-full px-3 py-1 font-mono text-[10px] font-semibold uppercase tracking-[0.15em] ${
        status === "new" ? "bg-ochre/20 text-clay" : status === "completed" ? "bg-forest/10 text-forest-leaf" : "bg-forest/10 text-forest"
    }`}>{status}</span>
);

const INV_STATUS_STYLES = {
    draft: "bg-stone-200 text-stone-600",
    sent: "bg-forest/10 text-forest",
    paid: "bg-forest text-bone",
    partially_paid: "bg-ochre/25 text-clay",
    overdue: "bg-clay/20 text-clay",
    cancelled: "bg-stone-200 text-stone-500 line-through",
};
const invLabel = (s) => (s || "draft").replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
const money2 = (n) => Number(n || 0).toLocaleString("en-AU", { minimumFractionDigits: 2 });

export default function Admin() {
    const [token, setToken] = useState(() => localStorage.getItem("lb_admin") || "");
    const [passcode, setPasscode] = useState("");
    const [bookings, setBookings] = useState(null);
    const [busy, setBusy] = useState(false);
    const [reviewSending, setReviewSending] = useState("");
    const [invoiceFor, setInvoiceFor] = useState(null);
    const [tab, setTab] = useState("admin");
    const [empName, setEmpName] = useState("");
    const [empPin, setEmpPin] = useState("");
    const [scheduleFor, setScheduleFor] = useState(null);
    const [adminTeam, setAdminTeam] = useState([]);
    const [view, setView] = useState("bookings");
    const [invoices, setInvoices] = useState([]);
    const [clients, setClients] = useState([]);
    const [clientModal, setClientModal] = useState(null);
    const [clientJob, setClientJob] = useState(null);
    const CLIENT_STATUS_STYLES = {
        lead: "bg-ochre/25 text-clay",
        follow_up: "bg-clay/15 text-clay",
        customer: "bg-forest/10 text-forest-leaf",
        not_interested: "bg-stone-200 text-stone-500",
    };
    const role = localStorage.getItem("lb_role") || "admin";
    const workerName = localStorage.getItem("lb_worker") || "";
    const logout = () => {
        localStorage.removeItem("lb_admin");
        localStorage.removeItem("lb_role");
        localStorage.removeItem("lb_worker");
        setToken("");
    };

    const load = useCallback(async (t) => {
        try {
            const [{ data: b }, { data: tm }, { data: inv }, { data: cl }] = await Promise.all([
                axios.get(`${API}/bookings`, { headers: { Authorization: `Bearer ${t}` } }),
                axios.get(`${API}/team`, { headers: { Authorization: `Bearer ${t}` } }),
                axios.get(`${API}/invoices`, { headers: { Authorization: `Bearer ${t}` } }),
                axios.get(`${API}/clients`, { headers: { Authorization: `Bearer ${t}` } }),
            ]);
            setBookings(b.bookings);
            setAdminTeam(tm.workers || []);
            setInvoices(inv.invoices || []);
            setClients(cl.clients || []);
        } catch {
            localStorage.removeItem("lb_admin");
            localStorage.removeItem("lb_role");
            localStorage.removeItem("lb_worker");
            setToken("");
        }
    }, []);

    useEffect(() => { if (token) load(token); }, [token, load]);

    const login = async (e) => {
        e.preventDefault();
        setBusy(true);
        try {
            const { data } = await axios.post(`${API}/admin/login`, { passcode });
            localStorage.setItem("lb_admin", data.token);
            localStorage.setItem("lb_role", "admin");
            localStorage.removeItem("lb_worker");
            setToken(data.token);
        } catch {
            toast.error("Incorrect passcode.");
        } finally {
            setBusy(false);
        }
    };

    const employeeLogin = async (e) => {
        e.preventDefault();
        setBusy(true);
        try {
            const { data } = await axios.post(`${API}/employee/login`, { name: empName, pin: empPin });
            localStorage.setItem("lb_admin", data.token);
            localStorage.setItem("lb_role", "employee");
            localStorage.setItem("lb_worker", data.worker);
            window.location.href = "/admin/scheduler";
        } catch {
            toast.error("Incorrect name or PIN.");
        } finally {
            setBusy(false);
        }
    };

    const setStatus = async (id, status) => {
        try {
            await axios.patch(`${API}/bookings/${id}`, { status }, { headers: { Authorization: `Bearer ${token}` } });
            setBookings((prev) => prev.map((b) => (b.id === id ? { ...b, status } : b)));
        } catch {
            toast.error("Could not update status.");
        }
    };

    const sendReview = async (id) => {
        setReviewSending(id);
        try {
            await axios.post(`${API}/bookings/${id}/send-review`, {}, { headers: { Authorization: `Bearer ${token}` } });
            toast.success("Review request email sent to the customer.");
        } catch {
            toast.error("Could not send the email — please try again.");
        } finally {
            setReviewSending("");
        }
    };

    const deleteBooking = async (id) => {
        if (!window.confirm("Delete this booking permanently?")) return;
        try {
            await axios.delete(`${API}/bookings/${id}`, { headers: { Authorization: `Bearer ${token}` } });
            setBookings((prev) => prev.filter((b) => b.id !== id));
            toast.success("Booking deleted.");
        } catch {
            toast.error("Could not delete the booking.");
        }
    };

    if (!token) {
        return (
            <main data-testid="admin-page" className="flex min-h-screen items-center justify-center bg-bone px-6 pt-16">
                <div className="w-full max-w-sm rounded-3xl border border-forest/10 bg-bone-card p-8">
                    <span className="flex h-12 w-12 items-center justify-center rounded-full bg-forest text-bone"><Lock size={18} /></span>
                    <h1 className="mt-5 font-display text-2xl font-bold text-ink">Lawn Busters Admin</h1>
                    <div className="mt-5 grid grid-cols-2 gap-2">
                        <button type="button" onClick={() => setTab("admin")} data-testid="admin-tab-admin" aria-pressed={tab === "admin"}
                            className={`rounded-full px-4 py-2.5 text-xs font-bold transition-colors ${tab === "admin" ? "bg-forest text-bone" : "border border-forest/20 text-forest hover:bg-bone-subtle"}`}>
                            Admin
                        </button>
                        <button type="button" onClick={() => setTab("employee")} data-testid="admin-tab-employee" aria-pressed={tab === "employee"}
                            className={`rounded-full px-4 py-2.5 text-xs font-bold transition-colors ${tab === "employee" ? "bg-forest text-bone" : "border border-forest/20 text-forest hover:bg-bone-subtle"}`}>
                            Team Member
                        </button>
                    </div>
                    {tab === "admin" ? (
                        <form onSubmit={login} className="mt-5">
                            <p className="text-sm text-moss">Full access to bookings, the job scheduler, team and settings.</p>
                            <input type="password" required value={passcode} onChange={(e) => setPasscode(e.target.value)}
                                data-testid="admin-passcode-input" placeholder="Admin passcode"
                                className="mt-4 w-full rounded-xl border border-forest/20 bg-bone px-4 py-3 text-sm outline-none focus:border-forest" />
                            <button type="submit" disabled={busy} data-testid="admin-login-button"
                                className="mt-4 w-full rounded-full bg-forest px-6 py-3 text-sm font-bold text-bone transition-colors hover:bg-forest-deep disabled:opacity-60">
                                {busy ? "Checking..." : "Log In"}
                            </button>
                        </form>
                    ) : (
                        <form onSubmit={employeeLogin} className="mt-5">
                            <p className="text-sm text-moss">View-only access to your scheduled jobs. Ask the boss for your PIN.</p>
                            <input required value={empName} onChange={(e) => setEmpName(e.target.value)}
                                data-testid="employee-name-input" placeholder="Your name (e.g. Josh)"
                                className="mt-4 w-full rounded-xl border border-forest/20 bg-bone px-4 py-3 text-sm outline-none focus:border-forest" />
                            <input type="password" required value={empPin} onChange={(e) => setEmpPin(e.target.value)}
                                data-testid="employee-pin-input" placeholder="Your PIN"
                                className="mt-3 w-full rounded-xl border border-forest/20 bg-bone px-4 py-3 text-sm outline-none focus:border-forest" />
                            <button type="submit" disabled={busy} data-testid="employee-login-button"
                                className="mt-4 w-full rounded-full bg-forest px-6 py-3 text-sm font-bold text-bone transition-colors hover:bg-forest-deep disabled:opacity-60">
                                {busy ? "Checking..." : "View My Jobs"}
                            </button>
                        </form>
                    )}
                </div>
            </main>
        );
    }

    if (role === "employee") {
        return (
            <main data-testid="admin-page" className="flex min-h-screen items-center justify-center bg-bone px-6 pt-16">
                <div className="w-full max-w-sm rounded-3xl border border-forest/10 bg-bone-card p-8 text-center">
                    <h1 className="font-display text-2xl font-bold text-ink">Hi {workerName || "there"}</h1>
                    <p className="mt-3 text-sm text-moss">
                        You're logged in with view-only access. The bookings inbox is admin-only — your jobs are in
                        the Job Scheduler.
                    </p>
                    <Link to="/admin/scheduler" data-testid="employee-scheduler-link"
                        className="mt-6 inline-block rounded-full bg-forest px-6 py-3 text-sm font-bold text-bone transition-colors hover:bg-forest-deep">
                        View Job Scheduler
                    </Link>
                    <button onClick={logout} data-testid="admin-logout-button"
                        className="mt-4 text-xs font-bold text-forest underline underline-offset-4">
                        Log out
                    </button>
                </div>
            </main>
        );
    }

    return (
        <main data-testid="admin-page" className="min-h-screen bg-bone px-6 pb-20 pt-24">
            <div className="mx-auto max-w-5xl">
                <div className="flex flex-wrap items-center justify-between gap-4">
                    <div>
                        <p className="eyebrow text-forest-leaf">Admin</p>
                        <h1 className="mt-2 font-display text-3xl font-bold text-ink">Booking Requests</h1>
                    </div>
                    <div className="flex gap-3">
                        <Link to="/admin/scheduler" data-testid="admin-scheduler-link"
                            className="flex items-center gap-2 rounded-full bg-forest px-4 py-2 text-xs font-bold text-bone transition-colors hover:bg-forest-deep">
                            <CalendarDays size={13} /> Job Scheduler
                        </Link>
                        <button onClick={() => load(token)} data-testid="admin-refresh-button"
                            className="flex items-center gap-2 rounded-full border border-forest/20 px-4 py-2 text-xs font-bold text-forest hover:bg-bone-subtle">
                            <RefreshCw size={13} /> Refresh
                        </button>
                        <button onClick={logout} data-testid="admin-logout-button"
                            className="flex items-center gap-2 rounded-full border border-forest/20 px-4 py-2 text-xs font-bold text-forest hover:bg-bone-subtle">
                            <LogOut size={13} /> Log out
                        </button>
                    </div>
                </div>

                <div className="mt-8 flex gap-2">
                    <button onClick={() => setView("bookings")} data-testid="tab-bookings" aria-pressed={view === "bookings"}
                        className={`rounded-full px-5 py-2.5 text-xs font-bold transition-colors ${view === "bookings" ? "bg-forest text-bone" : "border border-forest/20 text-forest hover:bg-bone-subtle"}`}>
                        Bookings
                    </button>
                    <button onClick={() => setView("invoices")} data-testid="tab-invoices" aria-pressed={view === "invoices"}
                        className={`rounded-full px-5 py-2.5 text-xs font-bold transition-colors ${view === "invoices" ? "bg-forest text-bone" : "border border-forest/20 text-forest hover:bg-bone-subtle"}`}>
                        Invoices
                        {invoices.length > 0 && (
                            <span className="ml-1.5 rounded-full bg-ochre px-1.5 py-0.5 text-[9px] font-bold text-forest-night">{invoices.length}</span>
                        )}
                    </button>
                    <button onClick={() => setView("clients")} data-testid="tab-clients" aria-pressed={view === "clients"}
                        className={`flex items-center gap-1.5 rounded-full px-5 py-2.5 text-xs font-bold transition-colors ${view === "clients" ? "bg-forest text-bone" : "border border-forest/20 text-forest hover:bg-bone-subtle"}`}>
                        <Users size={12} /> Clients
                        {clients.length > 0 && (
                            <span className="rounded-full bg-ochre px-1.5 py-0.5 text-[9px] font-bold text-forest-night">{clients.length}</span>
                        )}
                    </button>
                </div>

                {view === "clients" ? (
                    <div data-testid="admin-clients-list" className="mt-8">
                        <div className="flex flex-wrap items-center justify-between gap-3">
                            <p className="text-sm text-moss">
                                {clients.length} client{clients.length === 1 ? "" : "s"} tracked — door-to-door leads and customers.
                            </p>
                            <button onClick={() => setClientModal("new")} data-testid="add-client-button"
                                className="flex items-center gap-2 rounded-full bg-forest px-5 py-2.5 text-xs font-bold text-bone transition-colors hover:bg-forest-deep">
                                <Plus size={14} /> Add Client
                            </button>
                        </div>
                        {clients.length === 0 ? (
                            <p data-testid="admin-clients-empty" className="mt-6 rounded-3xl border border-dashed border-forest/25 p-16 text-center text-sm text-moss">
                                No clients yet — add your door-to-door leads here and schedule them when they&apos;re ready.
                            </p>
                        ) : (
                            <div className="mt-5 space-y-3">
                                {clients.map((cl) => (
                                    <div key={cl.id} data-testid={`admin-client-${cl.id}`}
                                        className="flex flex-wrap items-center gap-x-5 gap-y-2 rounded-2xl border border-forest/10 bg-bone-card p-5">
                                        <div className="min-w-[150px] flex-1">
                                            <p className="font-display text-base font-bold text-ink">{cl.name}</p>
                                            <p className="text-xs text-moss">{cl.address || "No address"}</p>
                                            {cl.notes && <p className="mt-1 max-w-md truncate text-xs italic text-moss">{cl.notes}</p>}
                                        </div>
                                        {cl.phone && (
                                            <a href={`tel:${cl.phone}`} className="flex items-center gap-1.5 text-xs font-semibold text-forest">
                                                <Phone size={12} /> {cl.phone}
                                            </a>
                                        )}
                                        {cl.services.length > 0 && (
                                            <div className="flex flex-wrap gap-1.5">
                                                {cl.services.map((s) => (
                                                    <span key={s} className="rounded-full bg-forest/10 px-2.5 py-1 text-[10px] font-bold text-forest">{s}</span>
                                                ))}
                                            </div>
                                        )}
                                        <span className={`rounded-full px-3 py-1 font-mono text-[10px] font-semibold uppercase tracking-[0.12em] ${CLIENT_STATUS_STYLES[cl.status]}`}>
                                            {CLIENT_STATUS_LABELS[cl.status]}
                                        </span>
                                        <div className="flex gap-2">
                                            <button onClick={() => setClientModal(cl)} data-testid={`client-edit-${cl.id}`}
                                                className="flex items-center gap-1.5 rounded-full border border-forest/25 px-3 py-2 text-xs font-bold text-forest hover:bg-bone-subtle">
                                                <Pencil size={12} /> Edit
                                            </button>
                                            <button onClick={() => setClientJob({ customer_name: cl.name, address: cl.address, phone: cl.phone, email: cl.email, services: cl.services, notes: cl.notes })}
                                                data-testid={`client-schedule-${cl.id}`}
                                                className="flex items-center gap-1.5 rounded-full bg-forest px-3 py-2 text-xs font-bold text-bone hover:bg-forest-deep">
                                                <CalendarDays size={12} /> Schedule Job
                                            </button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                ) : view === "invoices" ? (
                    <div data-testid="admin-invoices-list" className="mt-8 space-y-3">
                        {invoices.length === 0 ? (
                            <p data-testid="admin-invoices-empty" className="rounded-3xl border border-dashed border-forest/25 p-16 text-center text-sm text-moss">
                                No invoices yet — create one from any booking or job.
                            </p>
                        ) : (
                            <>
                                <div className="flex flex-wrap gap-3">
                                    {(() => {
                                        const outstanding = invoices.filter((i) => ["sent", "overdue", "partially_paid"].includes(i.status_display || i.status)).reduce((a, i) => a + (i.total || 0), 0);
                                        const paidTotal = invoices.filter((i) => (i.status_display || i.status) === "paid").reduce((a, i) => a + (i.total || 0), 0);
                                        return (
                                            <>
                                                <span data-testid="invoices-stat-count" className="rounded-full border border-forest/15 bg-bone-card px-4 py-2 text-xs font-bold text-forest">
                                                    {invoices.length} invoice{invoices.length === 1 ? "" : "s"}
                                                </span>
                                                <span data-testid="invoices-stat-outstanding" className="rounded-full border border-clay/30 bg-clay/10 px-4 py-2 text-xs font-bold text-clay">
                                                    ${money2(outstanding)} outstanding
                                                </span>
                                                <span data-testid="invoices-stat-paid" className="rounded-full border border-forest/15 bg-forest/5 px-4 py-2 text-xs font-bold text-forest-leaf">
                                                    ${money2(paidTotal)} paid
                                                </span>
                                            </>
                                        );
                                    })()}
                                </div>
                                {invoices.map((inv) => {
                                    const st = inv.status_display || inv.status;
                                    return (
                                        <button key={inv.id} onClick={() => setInvoiceFor({ inv })} data-testid={`admin-invoice-row-${inv.id}`}
                                            className="flex w-full flex-wrap items-center gap-x-5 gap-y-2 rounded-2xl border border-forest/10 bg-bone-card p-5 text-left transition-transform duration-200 hover:-translate-y-0.5">
                                            <div className="min-w-[110px]">
                                                <p className="font-mono text-[10px] font-semibold uppercase tracking-wider text-moss">{inv.invoice_no}</p>
                                                <p className="mt-0.5 text-[11px] text-moss">{inv.issue_date}</p>
                                            </div>
                                            <div className="min-w-[150px] flex-1">
                                                <p className="font-display text-base font-bold text-ink">{inv.customer_name}</p>
                                                <p className="text-xs text-moss">{(inv.items || []).map((i) => i.description).filter(Boolean).join(" · ") || "Services"}</p>
                                            </div>
                                            <span className="rounded-full bg-forest/10 px-2.5 py-1 font-mono text-[9px] font-semibold uppercase tracking-wider text-forest">{inv.source_type}</span>
                                            <p data-testid={`admin-invoice-total-${inv.id}`} className="font-display text-lg font-bold text-forest">${money2(inv.total)}</p>
                                            <span data-testid={`admin-invoice-status-${inv.id}`} className={`rounded-full px-3 py-1 font-mono text-[10px] font-semibold uppercase tracking-[0.12em] ${INV_STATUS_STYLES[st] || INV_STATUS_STYLES.draft}`}>
                                                {invLabel(st)}
                                            </span>
                                            {inv.sent_at && (
                                                <span className="text-[10px] font-semibold text-moss">Sent {new Date(inv.sent_at).toLocaleDateString("en-AU")}</span>
                                            )}
                                        </button>
                                    );
                                })}
                            </>
                        )}
                    </div>
                ) : !bookings ? (
                    <p className="mt-16 text-center text-sm text-moss">Loading bookings...</p>
                ) : bookings.length === 0 ? (
                    <p data-testid="admin-bookings-empty" className="mt-16 rounded-3xl border border-dashed border-forest/25 p-16 text-center text-sm text-moss">
                        No booking requests yet. New submissions from the website will appear here.
                    </p>
                ) : (
                    <div data-testid="admin-bookings-table" className="mt-10 space-y-4">
                        {bookings.map((b) => (
                            <div key={b.id} data-testid={`admin-booking-${b.id}`} className="rounded-3xl border border-forest/10 bg-bone-card p-6">
                                <div className="flex flex-wrap items-start justify-between gap-4">
                                    <div>
                                        <p className="font-display text-lg font-bold text-ink">{b.full_name}</p>
                                        <p className="mt-1 text-sm text-moss">
                                            <a className="font-semibold text-forest" href={`tel:${b.phone}`}>{b.phone}</a>
                                            {" · "}
                                            <a className="font-semibold text-forest" href={`mailto:${b.email}`}>{b.email}</a>
                                        </p>
                                        <p className="mt-1 text-sm text-moss">{b.address}</p>
                                    </div>
                                    <div className="flex items-center gap-3">
                                        {b.request_type === "quote" && (
                                            <span className="rounded-full bg-ochre/25 px-3 py-1 font-mono text-[10px] font-semibold uppercase tracking-[0.15em] text-clay">Free quote</span>
                                        )}
                                        <StatusBadge status={b.status} />
                                        <select value={b.status} onChange={(e) => setStatus(b.id, e.target.value)}
                                            data-testid={`admin-status-select-${b.id}`}
                                            className="rounded-full border border-forest/20 bg-bone px-3 py-2 text-xs font-bold text-forest outline-none">
                                            {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                                        </select>
                                        {b.status === "completed" && (
                                            <button onClick={() => sendReview(b.id)} disabled={reviewSending === b.id}
                                                data-testid={`admin-send-review-${b.id}`}
                                                className="flex items-center gap-1.5 rounded-full bg-forest px-3 py-2 text-xs font-bold text-bone transition-colors hover:bg-forest-deep disabled:opacity-60">
                                                <Mail size={12} /> {reviewSending === b.id ? "Sending..." : "Email review request"}
                                            </button>
                                        )}
                                        <button onClick={() => setInvoiceFor({ type: "booking", id: b.id })} data-testid={`admin-invoice-${b.id}`}
                                            className="flex items-center gap-1.5 rounded-full border border-forest/25 px-3 py-2 text-xs font-bold text-forest hover:bg-bone-subtle">
                                            <FileText size={12} /> Invoice
                                        </button>
                                        <button onClick={() => setScheduleFor(b)} data-testid={`admin-schedule-${b.id}`}
                                            className="flex items-center gap-1.5 rounded-full border border-forest/25 px-3 py-2 text-xs font-bold text-forest hover:bg-bone-subtle">
                                            <CalendarDays size={12} /> Schedule
                                        </button>
                                        <button onClick={() => deleteBooking(b.id)} data-testid={`admin-delete-${b.id}`} aria-label="Delete booking"
                                            className="flex items-center gap-1.5 rounded-full border border-clay/40 px-3 py-2 text-xs font-bold text-clay hover:bg-clay/10">
                                            <Trash2 size={12} />
                                        </button>
                                    </div>
                                </div>
                                <div className="mt-4 flex flex-wrap gap-2">
                                    {b.services.map((s) => (
                                        <span key={s} className="rounded-full bg-forest px-3 py-1 text-[11px] font-bold text-bone">{s}</span>
                                    ))}
                                </div>
                                <p className="mt-3 text-sm text-moss">
                                    Preferred: <span className="font-semibold text-ink">{b.preferred_date}</span> — {b.preferred_time}
                                </p>
                                <p data-testid={`admin-frequency-${b.id}`} className="mt-1 text-sm text-moss">
                                    Frequency: <span className="font-semibold text-ink">{b.frequency || "One-off"}</span>
                                </p>
                                {b.budget && (
                                    <p data-testid={`admin-budget-${b.id}`} className="mt-1 text-sm text-moss">
                                        Budget: <span className="font-semibold text-ink">{b.budget}</span>
                                    </p>
                                )}
                                {b.notes && <p className="mt-2 rounded-xl bg-bone-subtle p-3 text-sm text-moss">{b.notes}</p>}
                                <div className="mt-3 flex items-center justify-between">
                                    {b.photo_data ? (
                                        <a href={b.photo_data} target="_blank" rel="noreferrer" data-testid={`admin-photo-${b.id}`}>
                                            <img src={b.photo_data} alt={b.photo_name || "Customer photo"}
                                                className="h-16 w-16 rounded-lg border border-forest/15 object-cover" />
                                        </a>
                                    ) : (
                                        <span className="flex items-center gap-2 text-xs text-moss"><ImageOff size={13} /> No photo attached</span>
                                    )}
                                    <span className="font-mono text-[10px] uppercase tracking-[0.15em] text-moss">
                                        {new Date(b.created_at).toLocaleString("en-AU")}
                                    </span>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
            {scheduleFor && (
                <JobModal
                    job={null}
                    initial={{
                        customer_name: scheduleFor.full_name, address: scheduleFor.address,
                        phone: scheduleFor.phone, email: scheduleFor.email, date: scheduleFor.preferred_date,
                        services: scheduleFor.services, quote_price: scheduleFor.budget || "",
                        notes: scheduleFor.notes || "", job_status: "booked",
                    }}
                    team={adminTeam}
                    token={token}
                    onClose={() => setScheduleFor(null)}
                    onSaved={() => {
                        setStatus(scheduleFor.id, "scheduled");
                        setScheduleFor(null);
                        toast.success("Job scheduled — find it in the Job Scheduler.");
                    }}
                    onDeleted={() => setScheduleFor(null)}
                />
            )}
            {invoiceFor && (
                <InvoiceModal
                    invoice={invoiceFor.inv}
                    sourceType={invoiceFor.type}
                    sourceId={invoiceFor.id}
                    token={token}
                    onClose={() => {
                        setInvoiceFor(null);
                        if (view === "invoices") load(token);
                    }}
                    onDeleted={() => {
                        setInvoiceFor(null);
                        load(token);
                    }}
                />
            )}
            {clientModal && (
                <ClientModal
                    client={clientModal === "new" ? null : clientModal}
                    token={token}
                    onClose={() => setClientModal(null)}
                    onSaved={(saved) => {
                        setClients((prev) => (prev.some((c) => c.id === saved.id) ? prev.map((c) => (c.id === saved.id ? saved : c)) : [saved, ...prev]));
                        setClientModal(null);
                    }}
                    onDeleted={(id) => {
                        setClients((prev) => prev.filter((c) => c.id !== id));
                        setClientModal(null);
                    }}
                />
            )}
            {clientJob && (
                <JobModal
                    job={null}
                    initial={clientJob}
                    team={adminTeam}
                    token={token}
                    onClose={() => setClientJob(null)}
                    onSaved={() => {
                        setClientJob(null);
                        toast.success("Job scheduled — find it in the Job Scheduler.");
                    }}
                    onDeleted={() => setClientJob(null)}
                />
            )}
        </main>
    );
}
